/**
 * OC2-3562 — <PrescriptionGroup> (renders one Standard-variant group)
 *
 * Stateless functional component. No useState, no useEffect.
 */

import { getLinkedItems } from '@/components/contexts/KontentHomeConfigProvider'
import { CommonCopyUrlWrapper } from '@/components/ui/copy-to-clipboard/CommonCopyUrlWrapper'
import type { Mapping } from '@/types'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import PrescriptionWork from './PrescriptionWork'
import PrescriptionWorkBundle from './PrescriptionWorkBundle'
import type {
	PrescriptionGroup as PrescriptionGroupItem,
	PrescriptionWorkBundle as PrescriptionWorkBundleType,
	PrescriptionWork as PrescriptionWorkType,
} from './types'
import { shouldShowGroupTypeLabel } from './utils/isMixedType'

const TYPE_PRESCRIPTION_WORK = 'prescription_work'
const TYPE_PRESCRIPTION_WORK_BUNDLE = 'prescription_work_bundle'

const PRESCRIPTION_OPTION_PARAM = 'prescriptionoption'

export interface PrescriptionGroupProps {
	group: PrescriptionGroupItem
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	optionCodename?: string
}

export default function PrescriptionGroup({
	group,
	linkedItems,
	mappings,
	optionCodename,
}: PrescriptionGroupProps) {
	const children =
		getLinkedItems(group.elements.prescription_work, linkedItems) ?? []

	// Defensive: empty `prescription_work[]` → skip the entire group
	// Avoids an orphan heading + empty bullet list.
	if (children.length === 0) {
		return null
	}

	const showType = shouldShowGroupTypeLabel(group, linkedItems)
	const title = group.elements.title.value
	const groupAnchorId = `pg-${group.system.id}`
	const groupCopyUrlSearch = optionCodename
		? `?${PRESCRIPTION_OPTION_PARAM}=${encodeURIComponent(optionCodename)}`
		: ''
	const groupCopyUrl =
		typeof window !== 'undefined'
			? `${window.location.pathname}${groupCopyUrlSearch}#${groupAnchorId}`
			: `${groupCopyUrlSearch}#${groupAnchorId}`

	return (
		<div
			data-testid="prescription-group"
			data-kontent-item-id={group.system.id}
		>
			{title && (
				<CommonCopyUrlWrapper
					url={groupCopyUrl}
					className="nsw-h4 mb-3"
					id={groupAnchorId}
					data-kontent-element-codename="title"
				>
					{title}
				</CommonCopyUrlWrapper>
			)}

			<ul className="list-disc list-outside pl-6 space-y-3">
				{children.map((child) => {
					if (child.system.type === TYPE_PRESCRIPTION_WORK) {
						return (
							<PrescriptionWork
								key={child.system.id}
								work={child as PrescriptionWorkType}
								linkedItems={linkedItems}
								mappings={mappings}
								showTypeLabel={showType}
							/>
						)
					}
					if (child.system.type === TYPE_PRESCRIPTION_WORK_BUNDLE) {
						return (
							<PrescriptionWorkBundle
								key={child.system.id}
								bundle={child as PrescriptionWorkBundleType}
								linkedItems={linkedItems}
								mappings={mappings}
							/>
						)
					}
					// Defensive: unknown child type → skip (consistent with the
					// Phase 1 `dispatchVariant` defensive null-skip precedent).
					return null
				})}
			</ul>
		</div>
	)
}
